import * as Sentry from "@sentry/react";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { deepEqual } from "fast-equals";
import pDebounce from "p-debounce";
import * as React from "react";
import * as TypeCompiler from "typebox/compile";
import { type Role, User } from "@/shared/schema/user.ts";
import { toIso } from "@/shared/util/countryLanguageUtils.ts";
import { http, httpWarnChecked, queryClient } from "@/shared/util/fetch.tsx";
import { getCvPartner } from "@/shared/util/getCvPartner.ts";
import { invariant } from "@/shared/util/invariant.ts";
import { redirectTo } from "@/shared/util/routes.ts";
import { I18n } from "@/shared/util/translations.ts";
import { InterfaceSettingsProvider } from "./InterfaceSetting.tsx";
import { prefetchLoggedInStatus, useIsLoggedIn } from "./loggedInStatus.ts";
import { endSaveRequest, startSaveRequest } from "./networkState.ts";

const checker = TypeCompiler.Compile(User);

const queries = {
  fetch: queryOptions({
    queryKey: ["current-user"] as const,
    queryFn: ({ signal }) =>
      httpWarnChecked.get("/api/v1/currentuser", checker, { signal }),
  }),
};

/**
 * Modify an array of roles into an array of roles that needs to be shown to users
 */
export function displayable_roles(roles: readonly Role[]): readonly Role[] {
  if (roles.length === 1) {
    return roles;
  }
  if (roles.includes("internationalmanager")) {
    return ["internationalmanager"];
  }
  return roles.filter(
    (role) => role !== "consultant" && role !== "internationalmanager",
  );
}

export function useCurrentUser() {
  const isLoggedIn = useIsLoggedIn();

  const { data } = useQuery({
    ...queries.fetch,
    staleTime: 60_000,
    enabled: isLoggedIn === true,
    select: (user): ContextUser => {
      // We assume a company only belongs to one company group for now
      const companyGroupId = user.company_group_ids.at(0);

      return {
        ...user,

        companyGroupId,

        ...calculateLanguages(user),

        ...calculateRoles(user),
      };
    },
  });

  return { user: data, isLoggedIn };
}

export async function prefetchCurrentUser() {
  await Promise.allSettled([
    queryClient.query({ ...queries.fetch, staleTime: Infinity }),
    prefetchLoggedInStatus(),
  ]);
}

// Returns the user and lets a failed fetch reject, where prefetchCurrentUser
// discards both - so a loader can fail its route on it. Unlike that one it does
// not also warm the logged-in status query.
export async function ensureCurrentUser() {
  return queryClient.query({ ...queries.fetch, staleTime: Infinity });
}

export async function invalidateCurrentUser() {
  await queryClient.invalidateQueries(queries.fetch);
}

interface LanguageExtensions {
  readonly languageCode: string;
  readonly languageCodes: readonly string[];
  readonly uiLanguageCode: string;

  readonly languageCodeIso: string;
  readonly languageCodesIso: readonly string[];
  readonly uiLanguageCodeIso: string;

  readonly dateFormat: Intl.DateTimeFormat;
  readonly byteFormat: Intl.NumberFormat;
  readonly percentageFormat: Intl.NumberFormat;
  readonly languageFormat: Intl.DisplayNames;

  readonly preferredLanguage: string;
  readonly firstLanguage: string | undefined;
  readonly otherLanguages: readonly string[];

  readonly preferredLanguageIso: string;
  readonly firstLanguageIso: string | undefined;
  readonly otherLanguagesIso: readonly string[];
}

function calculateLanguages(user: User): LanguageExtensions {
  const {
    language_code: languageCode,
    language_codes: languageCodes,
    ui_language_code: uiLanguageCode,
    masterdata_languages,
  } = user;

  const languageCodeIso = toIso(languageCode);
  const languageCodesIso = languageCodes.map(toIso);
  const uiLanguageCodeIso = toIso(uiLanguageCode);

  const dateFormat = new Intl.DateTimeFormat(uiLanguageCodeIso, {
    dateStyle: "medium",
  });

  const percentageFormat = new Intl.NumberFormat(uiLanguageCodeIso, {
    style: "percent",
    maximumFractionDigits: 0,
  });

  const byteFormat = new Intl.NumberFormat(uiLanguageCodeIso, {
    notation: "compact",
    style: "unit",
    unit: "byte",
    unitDisplay: "narrow",
  });

  const languageFormat = new Intl.DisplayNames(uiLanguageCodeIso, {
    type: "language",
  });

  const [firstLanguage, ...otherLanguages] = masterdata_languages;
  const preferredLanguage = firstLanguage || "int";

  const preferredLanguageIso = toIso(preferredLanguage);
  const firstLanguageIso = firstLanguage ? toIso(firstLanguage) : undefined;
  const otherLanguagesIso = otherLanguages.map(toIso);

  return {
    languageCode,
    languageCodes,
    uiLanguageCode,

    languageCodeIso,
    languageCodesIso,
    uiLanguageCodeIso,

    dateFormat,
    percentageFormat,
    byteFormat,
    languageFormat,

    preferredLanguage,
    firstLanguage,
    otherLanguages,

    preferredLanguageIso,
    firstLanguageIso,
    otherLanguagesIso,
  };
}

interface RolesExtensions {
  readonly isInternationalmanager: boolean;
  readonly isCountrymanager: boolean;
  readonly isBidmanager: boolean;
  readonly isDepartmentmanager: boolean;
  readonly isReferenceManager: boolean;
  readonly isLimitedAccessUser: boolean;
  readonly isExternalUser: boolean;
  readonly isConsultant: boolean;

  readonly displayableRoles: readonly Role[];
  readonly displayableRolesUserNames: string;

  readonly grantableRoles: readonly Role[];
  readonly grantableApiUserRoles: readonly Role[];
}

function calculateRoles(user: User): RolesExtensions {
  const displayableRoles = displayable_roles(user.roles);

  const roles = new Set(user.roles);

  const isInternationalmanager = roles.has("internationalmanager");
  const isCountrymanager =
    !isInternationalmanager && roles.has("countrymanager");

  return {
    isInternationalmanager,
    isCountrymanager,
    isBidmanager: roles.has("bidmanager"),
    isDepartmentmanager: roles.has("departmentmanager"),
    isReferenceManager: roles.has("referencemanager"),
    isLimitedAccessUser: roles.has("limited_access"),
    isExternalUser: roles.has("external"),
    isConsultant: roles.has("consultant"),

    displayableRoles,
    displayableRolesUserNames: displayableRoles
      .map((role) => I18n.t(`role_labels.${role}`))
      .join(", "),

    grantableRoles: isCountrymanager
      ? ["external", "limited_access", "consultant", "countrymanager"]
      : isInternationalmanager
        ? [
            "external",
            "limited_access",
            "consultant",
            "countrymanager",
            "referencemanager",
            "internationalmanager",
            "departmentmanager",
            "bidmanager",
          ]
        : [],
    grantableApiUserRoles: (isCountrymanager
      ? (["consultant", "countrymanager"] as const)
      : isInternationalmanager
        ? (["consultant", "countrymanager", "internationalmanager"] as const)
        : []
    ).flatMap((role) => [`readonly_${role}`, role] as const),
  };
}

export interface ContextUser extends User, LanguageExtensions, RolesExtensions {
  readonly companyGroupId: string | undefined;
}

function useUpdateSentryUser(user: User | undefined) {
  const sentryUser = React.useMemo(() => {
    if (user == null) {
      return null;
    }

    return {
      id: user.id,
      email: user.email,
      name: user.name,
      roles: user.roles.join(","),
    };
  }, [user]);
  const userRef = React.useRef<typeof sentryUser>(null);

  React.useEffect(() => {
    const { faro } = getCvPartner();

    if (sentryUser == null) {
      Sentry.setUser(null);
      faro?.api.resetUser();
      return;
    }

    if (
      userRef.current != null &&
      userRef.current.id === sentryUser.id &&
      userRef.current.email === sentryUser.email &&
      userRef.current.name === sentryUser.name &&
      userRef.current.roles === sentryUser.roles
    ) {
      return;
    }

    userRef.current = sentryUser;

    const { roles, ...minimalUser } = sentryUser;
    Sentry.setUser(minimalUser);

    faro?.api.setUser({
      ...minimalUser,
      attributes: roles.length > 0 ? { roles } : undefined,
    });
  }, [sentryUser]);
}

const CurrentUserContext = React.createContext<ContextUser | null>(null);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isLoggedIn, user } = useCurrentUser();

  const isLoggedOut = isLoggedIn === false;

  useUpdateSentryUser(user);

  // Returning null is enough: `LoggedOutRedirector` is mounted outside this provider, so it stays
  // up and navigates to the timeout page properly for us.
  if (isLoggedOut || user == null) {
    return null;
  }

  return (
    <InterfaceSettingsProvider>
      <CurrentUserContext value={user}>{children}</CurrentUserContext>
    </InterfaceSettingsProvider>
  );
};

export function useLoggedInUser(): ContextUser {
  const user = React.use(CurrentUserContext);

  invariant(user, "User must be available");

  return user;
}

export function useCompanyGroupId() {
  const user = useLoggedInUser();

  return user.companyGroupId;
}

export function useLanguages() {
  const {
    languageCode,
    languageCodes,
    uiLanguageCode,

    languageCodeIso,
    languageCodesIso,
    uiLanguageCodeIso,

    dateFormat,
    percentageFormat,
    byteFormat,
    languageFormat,

    preferredLanguage,
    firstLanguage,
    otherLanguages,

    preferredLanguageIso,
    otherLanguagesIso,

    masterdata_languages,
  } = useLoggedInUser();

  return {
    languageCode,
    languageCodes,
    uiLanguageCode,

    languageCodeIso,
    languageCodesIso,
    uiLanguageCodeIso,

    dateFormat,
    percentageFormat,
    byteFormat,
    languageFormat,

    preferredLanguage,
    firstLanguage,
    otherLanguages,

    preferredLanguageIso,
    otherLanguagesIso,

    masterdata_languages,
  };
}

function useUpdateUser() {
  const queryClient = useQueryClient();
  const batchedUpdatesRef = React.useRef<readonly Partial<User>[]>([]);

  const { mutateAsync } = useMutation({
    mutationFn: (userUpdate: Partial<User>) =>
      http.put<User>("/api/v1/currentuser", { body: { user: userUpdate } }),
    scope: {
      id: "update-current-user",
    },
    onMutate: (userUpdate, { client }) => {
      client.setQueryData(queries.fetch.queryKey, (oldUser) => {
        if (oldUser == null) {
          return oldUser;
        }

        return { ...oldUser, ...userUpdate };
      });
    },
    onSuccess: (data, _vars, _onMutateResult, { client }) => {
      if (batchedUpdatesRef.current.length === 0) {
        client.setQueryData(queries.fetch.queryKey, data);
      }
    },
    onError: (_error, _vars, _onMutateResult, { client }) =>
      client.invalidateQueries(queries.fetch),
  });

  const debouncedUpdate = React.useMemo(
    () =>
      // debounce: https://github.com/facebook/react/issues/31290
      // eslint-disable-next-line react-hooks/refs
      pDebounce(async () => {
        const updates = batchedUpdatesRef.current.reduce<Partial<User>>(
          (acc, current) => ({ ...acc, ...current }),
          {},
        );

        batchedUpdatesRef.current = [];

        // Always end the save request, even if it fails.
        // try/finally is unsupported by the React Compiler, so use .finally()
        // eslint-disable-next-line unicorn/prefer-await
        await mutateAsync(updates).finally(() => endSaveRequest());
      }, 3000),
    [mutateAsync, batchedUpdatesRef],
  );

  // the accessing of a ref in `debouncedUpdate` trips up the compiler, so it
  // bails on optimizing this hook. Thus, the manual `useCallback`
  return React.useCallback(
    async (userUpdate: Partial<User>) => {
      let updated = true;

      // immediately update the cache, as the mutation is debounced
      queryClient.setQueryData(queries.fetch.queryKey, (oldUser) => {
        if (oldUser == null) {
          return oldUser;
        }
        const updatedUser = { ...oldUser, ...userUpdate };

        if (deepEqual(oldUser, updatedUser)) {
          updated = false;
          return oldUser;
        }

        return updatedUser;
      });

      if (!updated) {
        return;
      }

      const isFirstUpdate = batchedUpdatesRef.current.length === 0;
      batchedUpdatesRef.current = [...batchedUpdatesRef.current, userUpdate];

      // Start tracking save state if this is the first pending update
      if (isFirstUpdate) {
        startSaveRequest();
      }

      await debouncedUpdate();
    },
    [debouncedUpdate, queryClient],
  );
}

export function useResetSearchFilters() {
  const updateUser = useUpdateUser();

  return () =>
    updateUser({
      selected_office_ids: [],
      selected_tag_ids: [],
      include_officeless_reference_projects: false,
    });
}

export function useToggleExpandProposals() {
  const { expand_proposals_toggle } = useLoggedInUser();
  const updateUser = useUpdateUser();

  return () =>
    updateUser({ expand_proposals_toggle: !expand_proposals_toggle });
}

export function useUpdateSelectedOfficeIds() {
  const updateUser = useUpdateUser();

  return (officeIds: readonly string[]) =>
    updateUser({
      selected_office_ids: officeIds as string[],
      include_officeless_reference_projects: officeIds.length === 0,
    });
}

export function useUpdateSelectedTagIds() {
  const updateUser = useUpdateUser();

  return async (selectedTagIds: readonly string[]) =>
    updateUser({ selected_tag_ids: selectedTagIds as string[] });
}

export function useUpdateIncludeOfficelessReferenceProjects() {
  const updateUser = useUpdateUser();

  return (include: boolean) =>
    updateUser({ include_officeless_reference_projects: include });
}

export function useUpdateCountryAndOffice() {
  const updateUser = useUpdateUser();

  return async (
    updateHash: { country_id: string; office_id: string },
    redirectToPath: string,
  ) => {
    await updateUser(updateHash);

    redirectTo(redirectToPath);
  };
}

export function useSetInternationalToggle() {
  const updateUser = useUpdateUser();

  return (internationalToggle: "expand" | "hide") =>
    updateUser({ international_toggle: internationalToggle });
}

export function useUpdatePreferredDownloadFormat() {
  const updateUser = useUpdateUser();

  return (format: string) => updateUser({ preferred_download_format: format });
}

export function useShouldHidePreview() {
  const user = useLoggedInUser();

  return React.useCallback(
    (
      user_id: string,
      country_id: string | null,
      office_id: string,
      custom_tag_ids: readonly string[],
    ) => {
      if (!user.isLimitedAccessUser || user_id === user.id) {
        return false;
      }

      // Department manager of office
      if (user.role_allowed_office_ids?.includes(office_id)) {
        return false;
      }

      // Department manager of custom tag
      if (
        user.role_allowed_tag_ids?.some((value) =>
          custom_tag_ids.includes(value),
        )
      ) {
        return false;
      }

      // Country manager of country
      if (user.isCountrymanager && user.country_id === country_id) {
        return false;
      }

      return true;
    },
    [
      user.country_id,
      user.id,
      user.isCountrymanager,
      user.isLimitedAccessUser,
      user.role_allowed_office_ids,
      user.role_allowed_tag_ids,
    ],
  );
}
